import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Maybe } from 'yup';
import { Alert } from '../../../../../shared/lib/common/Alert';
import { NoDataFound } from '../../../../../shared/lib/common/NoDataFound';
import palette from '../../../../../shared/lib/theme/palette';
import { billingWrapper, hasMoreButton, hasMoreButtonWrapper, transactionHistoryHeaderCell } from '../../../../../shared/lib/theme/styleConstants';
import { LINKS, TRANSACTION_HISTORY_TABLE_HEADERS } from '../../constants';
import { useAppContext } from '../../contexts/AppContext';
import { useFromMobile } from '../../hooks/useFromMobile';
import { Invoice, useFetchCustomerInvoicesLazyQuery } from '../../../generated';
import { DisplayTransactionHistoryItem } from '../../../components/billingInvoice/DisplayTransactionHistoryItem';
import { TransactionHistorySkeleton } from '../../../components/skeletons/TransactionHistorySkeleton';
import { BillingInvoice } from '../../../components/billingInvoice';
import { DashboardContainer } from '../../../components/dashboard/DashboardContainer';
import { PageHeading } from '../../../../../shared/lib/common/PageHeading';
import { useNavigate } from 'react-router-dom';

const MobilePaymentHistory = () => {
  const fromMobile = useFromMobile();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};

  const [transactionHistory, setTransactionHistory] = useState<Maybe<Maybe<Invoice>[]> | undefined>([]);
  const [hasMoreValue, setHasMoreValue] = useState<boolean>(true);
  const [leftArrow, setLeftArrow] = useState<boolean>(false);
  const [firstId, setFirstId] = useState<string>('');
  const [lastId, setLastId] = useState<string>('');
  const [transactionPageCount, setTransactionPageCount] = useState<number>(1);

  const [fetchInvoices, { loading }] = useFetchCustomerInvoicesLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const { fetchCustomerInvoices } = data;
      const { data: fetchTransactionData, has_more } = fetchCustomerInvoices;
      setTransactionHistory(fetchTransactionData);
      setHasMoreValue(has_more);
    },
  });

  const fetchTransactions = async () => {
    customerStripeId &&
      (await fetchInvoices({
        variables: {
          customerInvoiceInput: {
            customer: customerStripeId,
            limit: 10,
            starting_after: lastId ? lastId : undefined,
            ending_before: firstId ? firstId : undefined,
          },
        },
      }));
  };

  useEffect(() => {
    (async () => fetchTransactions())();
  }, [currentUser, lastId, firstId]);

  const loadPrevValue = () => {
    const firstIndex = transactionHistory?.[0];
    const { id } = firstIndex ?? {};
    setFirstId(id || '');
    setTransactionPageCount(transactionPageCount - 1);
    setLastId('');
    setLeftArrow(true);
  };

  const loadMoreValue = () => {
    const lastIndex = transactionHistory && transactionHistory[transactionHistory.length - 1];
    const { id } = lastIndex ?? {};
    setLastId(id || '');
    setFirstId('');
    setTransactionPageCount(transactionPageCount + 1);
    setLeftArrow(false);
  };

  if (!fromMobile) {
    return (
      <DashboardContainer>
        <PageHeading color="blackOne" title="Payment History" subTitle="" />
        <BillingInvoice />
      </DashboardContainer>
    );
  }

  const isEmpty = transactionHistory?.length === 0 && !loading;

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 3, pb: 2, maxWidth: 600, mx: 'auto' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <ArrowBackIosNewIcon
            sx={{ fontSize: 18, color: palette.blackOne, cursor: 'pointer' }}
            onClick={() => navigate(LINKS.MOBILE_ACCOUNT)}
          />
          <Typography variant="h4" fontWeight={700} color={palette.blackOne}>
            Payment History
          </Typography>
        </Box>
      </Box>

      {/* Table card */}
      <Box sx={{ px: 2 }}>
        <Box sx={billingWrapper}>
          {isEmpty ? (
            <NoDataFound height="calc(100vh - 300px)" description="Record not found" />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {TRANSACTION_HISTORY_TABLE_HEADERS.map((item, index) => (
                      <TableCell key={index} sx={transactionHistoryHeaderCell}>
                        <Typography variant="subtitle2" sx={{ color: palette.blackTwo }}>
                          {item}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {loading ? (
                  <TransactionHistorySkeleton noOfRows={8} />
                ) : (
                  <TableBody>
                    {transactionHistory?.map((transaction, index) => (
                      <DisplayTransactionHistoryItem key={index} index={index} invoice={transaction} />
                    ))}
                  </TableBody>
                )}
              </Table>
            </Box>
          )}

          <Box sx={hasMoreButtonWrapper}>
            <Button sx={hasMoreButton} onClick={loadPrevValue} disabled={transactionPageCount - 1 === 0}>
              <ChevronLeftIcon fontSize="small" />
            </Button>
            <Button
              sx={hasMoreButton}
              onClick={loadMoreValue}
              disabled={transactionPageCount - 1 === 0 ? (leftArrow ? !leftArrow : leftArrow || !hasMoreValue) : !hasMoreValue}
            >
              <ChevronRightIcon fontSize="small" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MobilePaymentHistory;
